// src/components/scrambledtext.js
import React, { useRef } from 'react';
import PropTypes from 'prop-types';

const ScrambledText = ({ text, as: Component = 'span', className = '', ...props }) => {
  const textRef = useRef(null);
  const animationIdRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const originalTextRef = useRef(text);

  const duration = 800;

  const getRandomIndices = (length) => {
    const indices = Array.from({ length }, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  };

  const scrambleText = (element, characters, randomizedIndices, currentIndex) => {
    let scrambledText = '';
    for (let i = 0; i < characters.length; i++) {
      if (i <= currentIndex || characters[i] === ' ') {
        scrambledText += characters[i];
      } else {
        scrambledText += String.fromCharCode(Math.floor(Math.random() * 94) + 33);
      }
    }
    element.innerText = scrambledText;
  };

  const resetAnimation = (element, originalText) => {
    element.innerText = originalText;
  };

  const animateText = (element, originalText) => {
    const characters = originalText.split('');
    const randomizedIndices = getRandomIndices(characters.length);
    let startTime = null;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;

      if (progress >= duration) {
        isAnimatingRef.current = false;
        cancelAnimationFrame(animationIdRef.current);
        resetAnimation(element, originalText);
        return;
      }

      const currentIndex = Math.floor((progress / duration) * characters.length);
      scrambleText(element, characters, randomizedIndices, currentIndex);
      animationIdRef.current = requestAnimationFrame(animate);
    };

    animationIdRef.current = requestAnimationFrame(animate);
  };

  const handleMouseEnter = () => {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    animateText(textRef.current, originalTextRef.current);
  };

  const handleMouseLeave = () => {
    cancelAnimationFrame(animationIdRef.current);
    resetAnimation(textRef.current, originalTextRef.current);
    isAnimatingRef.current = false;
  };

  return (
    <Component
      ref={textRef}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {text}
    </Component>
  );
};

ScrambledText.propTypes = {
  text: PropTypes.string.isRequired,
  as: PropTypes.elementType,
  className: PropTypes.string,
};

export default ScrambledText;
